/**
 * Buyer on-chain settlement — USDC approve + TheGridMarketplace.fill().
 *
 * The buyer flow's final leg: after the backend authority signs the
 * SettlementPermit, the CONNECTED BUYER WALLET must (1) approve USDC to the
 * Marketplace for the EXACT price (only if allowance is insufficient) and then
 * (2) call `Marketplace.fill(Order, sellerSig, SettlementPermit, permitSig,
 * maxPrice)`. The agent never broadcasts — only the user's wallet does.
 *
 * Split for testability (mirrors usdc-approve.ts):
 *   - pure ABIs + tuple builders (buildFillOrder / buildFillPermit)
 *   - `BuySettlementController` — pure state machine with injected deps
 *   - `useBuySettlement()` — thin wagmi/viem wiring around the controller
 */
import { useCallback, useRef, useState } from "react";
import { useWriteContract, usePublicClient } from "wagmi";
import type { Abi } from "viem";

import { ERC20_APPROVE_ABI, isUserRejection } from "../../lib/usdc-approve";
import type { Listing, SettlementPermitData } from "../../lib/marketplace-api";

// ---------------------------------------------------------------------
// Minimal ABIs — allowance() read + fill() write ONLY.
// ---------------------------------------------------------------------
export const ERC20_ALLOWANCE_ABI = [
  {
    type: "function",
    name: "allowance",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
] as const satisfies Abi;

export const MARKETPLACE_FILL_ABI = [
  {
    type: "function",
    name: "fill",
    stateMutability: "nonpayable",
    inputs: [
      {
        name: "o",
        type: "tuple",
        components: [
          { name: "seller", type: "address" },
          { name: "collection", type: "address" },
          { name: "tokenId", type: "uint256" },
          { name: "currency", type: "address" },
          { name: "price", type: "uint256" },
          { name: "listingVersion", type: "uint256" },
          { name: "nonce", type: "uint256" },
          { name: "expiryRealTs", type: "uint256" },
        ],
      },
      { name: "sellerSig", type: "bytes" },
      {
        name: "p",
        type: "tuple",
        components: [
          { name: "orderHash", type: "bytes32" },
          { name: "buyer", type: "address" },
          { name: "floorPrice", type: "uint256" },
          { name: "expiryRealTs", type: "uint256" },
        ],
      },
      { name: "permitSig", type: "bytes" },
      { name: "maxPrice", type: "uint256" },
    ],
    outputs: [],
  },
] as const satisfies Abi;

// ---------------------------------------------------------------------
// Contract struct types + pure builders (byte-exact to the Stage-1 Order).
// ---------------------------------------------------------------------
export interface FillOrderStruct {
  seller: `0x${string}`;
  collection: `0x${string}`;
  tokenId: bigint;
  currency: `0x${string}`;
  price: bigint;
  listingVersion: bigint;
  nonce: bigint;
  expiryRealTs: bigint;
}

export interface FillPermitStruct {
  orderHash: `0x${string}`;
  buyer: `0x${string}`;
  floorPrice: bigint;
  expiryRealTs: bigint;
}

function hexAddr(a: string): `0x${string}` {
  return a.toLowerCase() as `0x${string}`;
}

/** Build the exact on-chain Order tuple from the authoritative listing. */
export function buildFillOrder(l: Listing): FillOrderStruct {
  return {
    seller: hexAddr(l.seller),
    collection: hexAddr(l.collection),
    tokenId: BigInt(l.token_id),
    currency: hexAddr(l.currency),
    price: BigInt(l.price_micro),
    listingVersion: BigInt(l.listing_version),
    nonce: BigInt(l.nonce),
    expiryRealTs: BigInt(l.expiry_real_ts),
  };
}

/** Build the exact on-chain SettlementPermit tuple from the permit response. */
export function buildFillPermit(p: SettlementPermitData): FillPermitStruct {
  return {
    orderHash: p.orderHash as `0x${string}`,
    buyer: hexAddr(p.buyer),
    floorPrice: BigInt(p.floorPrice),
    expiryRealTs: BigInt(p.expiryRealTs),
  };
}

// ---------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------
export type BuyStage = "allowance" | "approve" | "fill";

export type BuyState =
  | { kind: "idle" }
  | { kind: "checking_allowance" }
  | { kind: "awaiting_approve_wallet" }
  | { kind: "approving"; txHash: `0x${string}` }
  | { kind: "awaiting_fill_wallet" }
  | { kind: "filling"; txHash: `0x${string}` }
  | { kind: "completed"; txHash: `0x${string}` }
  | { kind: "approve_rejected" }
  | { kind: "approve_reverted"; txHash: `0x${string}` }
  | { kind: "fill_rejected" }
  | { kind: "fill_reverted"; txHash: `0x${string}` }
  | { kind: "error"; stage: BuyStage; error: string };

export interface FillArgs {
  marketplace: `0x${string}`;
  order: FillOrderStruct;
  sellerSig: `0x${string}`;
  permit: FillPermitStruct;
  permitSig: `0x${string}`;
  maxPrice: bigint;
  chainId: number;
}

export interface SettleInput {
  chainId: number;
  usdc: `0x${string}`;
  marketplace: `0x${string}`;
  buyer: `0x${string}`;
  /** Exact listing price in micro-USDC (the amount to approve + the maxPrice). */
  price: bigint;
  order: FillOrderStruct;
  sellerSig: `0x${string}`;
  permit: FillPermitStruct;
  permitSig: `0x${string}`;
  maxPrice: bigint;
}

export interface ReceiptOutcome {
  status: "success" | "reverted";
}

export interface BuyDeps {
  readAllowance: (
    usdc: `0x${string}`,
    owner: `0x${string}`,
    spender: `0x${string}`,
  ) => Promise<bigint>;
  approve: (p: {
    token: `0x${string}`;
    spender: `0x${string}`;
    amount: bigint;
    chainId: number;
  }) => Promise<`0x${string}`>;
  fill: (p: FillArgs) => Promise<`0x${string}`>;
  waitForReceipt: (hash: `0x${string}`) => Promise<ReceiptOutcome>;
  onStateChange?: (state: BuyState) => void;
}

const PENDING_KINDS: ReadonlyArray<BuyState["kind"]> = [
  "checking_allowance",
  "awaiting_approve_wallet",
  "approving",
  "awaiting_fill_wallet",
  "filling",
];

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export class BuySettlementController {
  private _state: BuyState = { kind: "idle" };

  constructor(private deps: BuyDeps) {}

  get state(): BuyState {
    return this._state;
  }

  get isPending(): boolean {
    return PENDING_KINDS.includes(this._state.kind);
  }

  reset(): void {
    if (this.isPending) return;
    this.setState({ kind: "idle" });
  }

  private setState(next: BuyState) {
    this._state = next;
    this.deps.onStateChange?.(next);
  }

  /**
   * Idempotent under double-click. Sequence:
   *   checking_allowance
   *     → (allowance < price) awaiting_approve_wallet → approving
   *          → receipt success  → continue
   *          → receipt reverted → approve_reverted (STOP; never fill)
   *          → wallet rejected  → approve_rejected  (STOP; never fill)
   *     → awaiting_fill_wallet → filling
   *          → receipt success  → completed
   *          → receipt reverted → fill_reverted (NEVER completed)
   *          → wallet rejected  → fill_rejected
   */
  async settle(input: SettleInput): Promise<void> {
    if (this.isPending) return;
    if (typeof input.price !== "bigint" || input.price <= 0n) {
      this.setState({ kind: "error", stage: "allowance", error: "invalid_price" });
      return;
    }

    // 1. allowance
    this.setState({ kind: "checking_allowance" });
    let allowance: bigint;
    try {
      allowance = await this.deps.readAllowance(input.usdc, input.buyer, input.marketplace);
    } catch (e) {
      this.setState({ kind: "error", stage: "allowance", error: errMsg(e) });
      return;
    }

    // 2. approve ONLY when insufficient — the EXACT price, never a delta/max.
    if (allowance < input.price) {
      this.setState({ kind: "awaiting_approve_wallet" });
      let hash: `0x${string}`;
      try {
        hash = await this.deps.approve({
          token: input.usdc,
          spender: input.marketplace,
          amount: input.price,
          chainId: input.chainId,
        });
      } catch (e) {
        this.setState(
          isUserRejection(e)
            ? { kind: "approve_rejected" }
            : { kind: "error", stage: "approve", error: errMsg(e) },
        );
        return;
      }
      this.setState({ kind: "approving", txHash: hash });
      let rc: ReceiptOutcome;
      try {
        rc = await this.deps.waitForReceipt(hash);
      } catch (e) {
        this.setState({ kind: "error", stage: "approve", error: errMsg(e) });
        return;
      }
      if (rc.status !== "success") {
        this.setState({ kind: "approve_reverted", txHash: hash });
        return; // fail-closed: NEVER fill after a failed approve
      }
    }

    // 3. fill — from the connected buyer wallet, exact Order/permit/sigs.
    this.setState({ kind: "awaiting_fill_wallet" });
    let fillHash: `0x${string}`;
    try {
      fillHash = await this.deps.fill({
        marketplace: input.marketplace,
        order: input.order,
        sellerSig: input.sellerSig,
        permit: input.permit,
        permitSig: input.permitSig,
        maxPrice: input.maxPrice,
        chainId: input.chainId,
      });
    } catch (e) {
      this.setState(
        isUserRejection(e)
          ? { kind: "fill_rejected" }
          : { kind: "error", stage: "fill", error: errMsg(e) },
      );
      return;
    }
    this.setState({ kind: "filling", txHash: fillHash });
    let frc: ReceiptOutcome;
    try {
      frc = await this.deps.waitForReceipt(fillHash);
    } catch (e) {
      this.setState({ kind: "error", stage: "fill", error: errMsg(e) });
      return;
    }
    this.setState(
      frc.status === "success"
        ? { kind: "completed", txHash: fillHash }
        : { kind: "fill_reverted", txHash: fillHash },
    );
  }
}

// ---------------------------------------------------------------------
// React hook — thin wagmi/viem wiring around the controller.
// ---------------------------------------------------------------------
export interface UseBuySettlementResult {
  state: BuyState;
  settle: (input: SettleInput) => Promise<void>;
  reset: () => void;
  isPending: boolean;
}

export function useBuySettlement(): UseBuySettlementResult {
  const [state, setState] = useState<BuyState>({ kind: "idle" });
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();

  const depsRef = useRef<Omit<BuyDeps, "onStateChange">>({
    readAllowance: async () => {
      throw new Error("public_client_not_ready");
    },
    approve: async () => {
      throw new Error("wallet_not_ready");
    },
    fill: async () => {
      throw new Error("wallet_not_ready");
    },
    waitForReceipt: async () => {
      throw new Error("public_client_not_ready");
    },
  });

  depsRef.current.readAllowance = async (usdc, owner, spender) => {
    if (publicClient == null) throw new Error("public_client_not_ready");
    const v = await publicClient.readContract({
      address: usdc,
      abi: ERC20_ALLOWANCE_ABI,
      functionName: "allowance",
      args: [owner, spender],
    });
    return v as bigint;
  };
  depsRef.current.approve = async ({ token, spender, amount, chainId }) => {
    const hash = (await writeContractAsync({
      address: token,
      abi: ERC20_APPROVE_ABI,
      functionName: "approve",
      args: [spender, amount],
      chainId,
    } as never)) as `0x${string}`;
    return hash;
  };
  depsRef.current.fill = async ({ marketplace, order, sellerSig, permit, permitSig, maxPrice, chainId }) => {
    const hash = (await writeContractAsync({
      address: marketplace,
      abi: MARKETPLACE_FILL_ABI,
      functionName: "fill",
      args: [order, sellerSig, permit, permitSig, maxPrice],
      chainId,
    } as never)) as `0x${string}`;
    return hash;
  };
  depsRef.current.waitForReceipt = async (hash) => {
    if (publicClient == null) throw new Error("public_client_not_ready");
    const rc = await publicClient.waitForTransactionReceipt({ hash });
    return { status: rc.status === "success" ? "success" : "reverted" };
  };

  const controllerRef = useRef<BuySettlementController | null>(null);
  if (controllerRef.current == null) {
    controllerRef.current = new BuySettlementController({
      readAllowance: (a, b, c) => depsRef.current.readAllowance(a, b, c),
      approve: (p) => depsRef.current.approve(p),
      fill: (p) => depsRef.current.fill(p),
      waitForReceipt: (h) => depsRef.current.waitForReceipt(h),
      onStateChange: (s) => setState(s),
    });
  }

  const settle = useCallback((input: SettleInput) => controllerRef.current!.settle(input), []);
  const reset = useCallback(() => controllerRef.current!.reset(), []);
  const isPending = PENDING_KINDS.includes(state.kind);

  return { state, settle, reset, isPending };
}
